/**
 * Resolve the best logo URL for a merchant.
 *
 * Existing dev seed data uses logo.clearbit.com URLs that no longer resolve
 * (Clearbit's free API was sunset after the HubSpot acquisition). For those
 * — and for any merchant missing a logo_url — we derive a working URL from
 * the merchant's website domain.
 *
 * Provider ordering: Google's s2/favicons first because it returns a result
 * for virtually any indexed domain (a small placeholder if no real favicon
 * exists), so the visible behavior is consistent. DuckDuckGo's ip3 service
 * sometimes returns higher-quality logos but 404s on a meaningful fraction
 * of mainstream merchants (cvs.com, dominos.com, ubereats.com, etc.) which
 * clutters the console with errors. Using DDG as a secondary "try for nicer
 * logo, then fall through" was producing visible failures and noise — Google
 * primary is the reliable default.
 */
// Hosts whose stored logo_url values never resolve and must be re-derived:
//  - logo.clearbit.com: free API sunset after the HubSpot acquisition.
//  - icons.duckduckgo.com: the ip3 favicon service 404s for a noisy fraction
//    of mainstream domains (counttire.com, cvs.com, ctheatres.com, dominos.com,
//    …), spamming the browser console — Perkvora bug 27-07 #1 (regression).
const DEAD_HOSTS = ['logo.clearbit.com', 'icons.duckduckgo.com'];

// A logo_url stored as a DuckDuckGo ip3 favicon still embeds the real domain
// (icons.duckduckgo.com/ip3/<domain>.ico); re-point it at Google's favicon
// service (returns 200 for any domain) so no 404 is ever requested.
const DDG_IP3_RE = /icons\.duckduckgo\.com\/ip3\/([^/?#]+?)\.ico/i;

function googleFavicon(domain) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

function domainOf(merchant) {
  const site = merchant?.website || merchant?.url || '';
  if (!site) return null;
  const m = site.match(/^https?:\/\/([^/?#]+)/i);
  return m ? m[1].toLowerCase() : null;
}

export function merchantLogoUrl(merchant) {
  if (!merchant) return null;
  const stored = merchant.logo_url;
  if (stored) {
    // Rewrite a stored DDG favicon to Google using its embedded domain, so the
    // logo still resolves even when the merchant has no separate website field.
    const ddg = stored.match(DDG_IP3_RE);
    if (ddg) return googleFavicon(ddg[1]);
    if (!DEAD_HOSTS.some(h => stored.includes(h))) return stored;
  }
  const domain = domainOf(merchant);
  return domain ? googleFavicon(domain) : null;
}

export function merchantLogoFallbackUrl(merchant) {
  // Used to chain to DuckDuckGo's ip3 service, but DDG 404s for a noisy
  // fraction of mainstream domains (cvs.com, ubereats.com, etc.) and
  // those 404s show up in the browser console even though the visual
  // fallback to the initial-letter avatar works fine — Madhan 06-29 #5
  // flagged the console noise. The avatar in MerchantLogo.jsx covers
  // every failure case, so just skip the second-pass HTTP request.
  return null;
}
